/* eslint-disable no-unused-vars */
import moment from 'moment';
import Vue from 'vue';
import { _ } from 'underscore';

const opts = { withCredentials: true };

// Amendment A5 (FR-026/FR-027): build the optional `?customerId=` query string for QueenBee
// reading GETs. Returns '' when the open customer's id is missing/empty so the parameter is
// omitted entirely (QueenBee then returns readTypeFlag = null on every reading; the UI must
// continue to render normally per FR-014/FR-027). No optional chaining (Vue 2.6 / NFR-005).
function buildCustomerQuery(rootState) {
  const customer =
    rootState && rootState.customers && rootState.customers.customer;
  const customerId = customer && customer.Id;
  return customerId ? '?customerId=' + encodeURIComponent(customerId) : '';
}

export const state = {
  readingsToggle: null,
  activeService: null,
  activeMeter: null,
  sscInfo: null,
  activeMeterInfo: null,
  activeConfigInfo: null,
  metersList: [],
  reading: null,
  advances: [],
  contractsByServiceIdentifier: [],
  showOnlyCustomer: true, // Probably doesn't need to be a Vuex prop
  showExcludedReadings: false, // Probably doesn't need to be a Vuex prop
  meterSerialFilter: 'All', // Probably doesn't need to be a Vuex prop
  isValid: false,
  override: false,
  isAdvancedAddress: false,
  serviceTenancies: null,
  pickerDate: moment().format("YYYY-MM-DD"),
  updateableSiteAddress: {
    Line1: null,
    Line2: null,
    Line3: null,
    Line4: null,
    PoBox: null,
    SubBuildingName: null,
    BuildingName: null,
    BuildingNumber: null,
    DependentThoroughfare: null,
    Thoroughfare: null,
    DoubleDependentLocality: null,
    DependentLocality: null,
    Town: null,
    County: null,
    Postcode: null,
  },
  advanceRAGRanges: [],
  advanceRangesAvailable: null, // null = unknown, true = available, false = not available
};

export const mutations = {
  setActiveService(state, service) {
    state.activeService = service;
    state.activeMeter = null;
    state.activeMeterInfo = null;
    state.activeConfigInfo = null;
  },
  cloneSiteAddress(state, site) {
    for (var prop in site) {
      if (prop != 'AddressLines') {
        state.updateableSiteAddress[prop] = site[prop];
      }
    }
    state.isAdvancedAddress =
      state.updateableSiteAddress.Line1 == null &&
      state.updateableSiteAddress.Line2 == null &&
      state.updateableSiteAddress.Line3 == null &&
      state.updateableSiteAddress.Line4 == null;
  },
  resetSiteAddress(state) {
    state.updateableSiteAddress = {
      Line1: null,
      Line2: null,
      Line3: null,
      Line4: null,
      PoBox: null,
      SubBuildingName: null,
      BuildingName: null,
      BuildingNumber: null,
      DependentThoroughfare: null,
      Thoroughfare: null,
      DoubleDependentLocality: null,
      DependentLocality: null,
      Town: null,
      County: null,
      Postcode: null,
    };
  },
  updateAddressType(state, bool) {
    state.isAdvancedAddress = bool;
  },
  setActiveMeter(state, meter) {
    state.activeMeter = meter;
  },
  setActiveMeterInfo(state, meter) {
    state.activeMeterInfo = meter;
  },
  setActiveConfigInfo(state, config) {
    state.activeConfigInfo = config;
  },
  updateSscInfo(state, sscInfo) {
    state.sscInfo = sscInfo;
  },
  updateReadingsToggle(state, service) {
    state.readingsToggle = service;
  },
  updateShowOnlyCustomer(state, bool) {
    state.showOnlyCustomer = bool;
  },
  updateShowExcludedReadings(state, bool) {
    state.showExcludedReadings = bool;
  },
  updateMeterSerialFilter(state, filter) {
    state.meterSerialFilter = filter;
  },
  updateReadings(state, readings) {
    state.activeService.Readings = readings;
  },
  updateLatestReading(state, readings) {
    var latest = _.sortBy(readings, 'ReadingDate').reverse()[0];
    state.activeMeter.LatestReading = latest;
  },
  updateMetersList(state, meters) {
    state.metersList.push(meters);
  },
  setAdvances(state, advances) {
    state.advances = advances;
  },
  updateAdvances(state, advances) {
    if (state.advances.some((a) => a.AdvanceDate == advances.AdvanceDate)) {
      state.advances = state.advances.map(
        (a) => (a.AdvanceDate != advances.AdvanceDate ? a : advances)
      );
    } else {
      state.advances.push(advances);
    }
  },
  setAdvanceRangesAvailable(state, isAvailable) {
    state.advanceRangesAvailable = isAvailable;
  },
  updateReading(state, reading) {
    state.reading = reading;
  },
  updateIsValid(state, bool) {
    state.isValid = bool;
  },
  updateOverride(state, bool) {
    state.override = bool;
  },
  updateContractsByServiceIdentifier(state, contracts) {
    state.contractsByServiceIdentifier = contracts;
  },
  updateSubmitStatus(state, status) {
    state.submitStatus = status;
  },
  updatePickerDate(state, date) {
    state.pickerDate = date;
  },
  setAdvanceRAGRanges(state, ranges) {
    state.advanceRAGRanges = ranges;
  },
};

export const actions = {
  addNewReading({ state }, reading) {
    let url = `https://${Vue.config.honeycomb.readingsbot[
      state.activeService.Service.ServiceType.toLowerCase()
    ].domain
      }/api/v1/readings`;
    return Vue.prototype.$http.post(url, reading, { withCredentials: true });
  },

  async loadServiceTenancies(_, { ServiceIdentifier }) {
    const domain = `https://${Vue.config.honeycomb.contractbot.domain}`;
    const path = `/api/v1/tenancies/byServiceIdentifier/${ServiceIdentifier}`;
    const url = domain + path;
    try {
      return await Vue.prototype.$http.get(url, opts);
    } catch {
      return { data: null };
    }
  },

  async getContractsByServiceIdentifier({ commit }, service) {
    if (!service || !service.Service || !service.Service.ServiceIdentifier) {
      console.warn("getContractsByServiceIdentifier: Invalid service object provided.", service);
      return;
    }
    const response = await Vue.prototype.$http.get(
      'https://' +
      Vue.config.honeycomb.contractbot.domain +
      '/api/v1/contracts/byServiceIdentifier/' +
      service.Service.ServiceIdentifier,
      {
        withCredentials: true,
      }
    );
    commit('updateContractsByServiceIdentifier', response.data);
    commit('updateMeterSerialFilter', 'All');
    commit('updateShowExcludedReadings', false);
    commit('updateShowOnlyCustomer', true);
  },

  changeActiveService({ dispatch, commit }, service) {
    commit('setActiveService', service);
    if (service && service.Service && service.Service.ServiceIdentifier) {
      dispatch('getContractsByServiceIdentifier', service);
    }

    // For electricity services, always try to get advance ranges regardless of HhMeterPoint
    // QueenBee will authoritatively determine if advance ranges are available
    if (service && service.Service && service.Service.ServiceType === 'Electricity' && service.MeterPoint) {
      commit('setAdvances', []);
      commit('setAdvanceRangesAvailable', null); // Reset to unknown when changing services

      const mpan = service.MeterPoint.Mpan;
      if (mpan) {
        dispatch('getAdvancesForDate', {
          serviceIdentifier: mpan,
          date: moment().format('YYYY-MM-DD'),
        });
        dispatch('getAdvancesRAG', {
          serviceIdentifier: mpan,
        });
      }
    } else {
      // If not an electricity service, reset advance ranges availability
      commit('setAdvanceRangesAvailable', null);
    }
  },

  async refreshReadings({ state, commit, rootState }) {
    let meterPointSharedId = null;
    let fuel = null;

    if (state.activeService.Service.ServiceType === 'Gas') {
      meterPointSharedId =
        state.activeService.Service.GasDetails.MeterPointSharedId;
      fuel = 'gas';
    } else {
      meterPointSharedId =
        state.activeService.Service.ElectricityDetails.MeterPointSharedId;
      fuel = 'elec';
    }
    // Amendment A5 / FR-026/FR-027: anchor QueenBee flag derivation to the open customer.
    const customerQuery = buildCustomerQuery(rootState);
    const response = await Vue.prototype.$http.get(
      'https://' +
      Vue.config.honeycomb.queenbee.domain +
      '/api/v1.0/readings/' +
      fuel +
      '/' +
      meterPointSharedId +
      customerQuery,
      {
        withCredentials: true,
      }
    );
    commit('updateReadings', response.data);
  },

  async refreshLatestReading({ state, commit }) {
    var domain =
      state.activeMeter.ServiceType == 'Gas'
        ? Vue.config.honeycomb.readingsbot.gas.domain
        : Vue.config.honeycomb.readingsbot.electricity.domain;
    const response = await Vue.prototype.$http.get(
      'https://' +
      domain +
      '/api/v1/readings?meterPointSharedId=' +
      state.activeMeter.MeterPointSharedId,
      {
        withCredentials: true,
      }
    );
    commit('updateLatestReading', response.data.Records);
  },

  changeActiveMeter({ commit }, meter) {
    commit('setActiveMeter', meter);
  },
  changeActiveMeterInfo({ commit }, meter) {
    commit('setActiveMeterInfo', meter);
  },
  changeActiveConfigInfo({ commit }, config) {
    commit('setActiveConfigInfo', config);
  },
  refreshServiceList({ rootState, dispatch }) {
    rootState.contracts.services = [];
    dispatch('getServiceList');
  },
  getServiceList({ rootState, dispatch }) {
    rootState.contracts.contracts.forEach((contract) => {
      contract.Services.forEach((service) => {
        dispatch('getMeterDetails', {
          Service: service,
          Contract: contract,
        });
      });
    });
  },

  async getMeterDetails({ dispatch, commit, rootState }, payload) {
    let meterPointSharedId = null;
    let registrationSharedId = null;
    let tenancySharedId = null;
    let flowbotDomain = null;

    const serviceType = payload.Service.ServiceType;

    const { data: serviceTenancies } = await dispatch(
      'loadServiceTenancies',
      payload.Service
    );

    tenancySharedId = payload.Service[`${serviceType}Details`].TenancySharedId;

    const tenancy = serviceTenancies.find(
      (t) => t.TenancySharedId === tenancySharedId
    );

    meterPointSharedId =
      tenancy[`${serviceType}TenancyDetails`].MeterPointSharedId;
    registrationSharedId =
      tenancy[`${serviceType}TenancyDetails`].RegistrationSharedId;

    flowbotDomain =
      Vue.config.honeycomb.flowbot[serviceType.toLowerCase()].domain;

    const res = await Promise.all([
      dispatch('getSite', payload),
      dispatch('getMeters', {
        flowbotDomain,
        meterPointSharedId,
        payload,
      }),
      dispatch('getMeterPoint', {
        flowbotDomain,
        meterPointSharedId,
      }),
      dispatch('getRegistration', {
        flowbotDomain,
        registrationSharedId,
      }),
    ]);

    const fuel = serviceType === 'Gas' ? 'gas' : 'elec';
    // Amendment A5 / FR-026/FR-027: anchor QueenBee flag derivation to the open customer.
    const customerQuery = buildCustomerQuery(rootState);
    const readingsResponse = await Vue.prototype.$http.get(
      'https://' +
      Vue.config.honeycomb.queenbee.domain +
      '/api/v1.0/readings/' +
      fuel +
      '/' +
      meterPointSharedId +
      customerQuery,
      {
        withCredentials: true,
      }
    );
    const readings = readingsResponse.data;

    commit('updateServices', {
      Service: payload.Service,
      Contract: payload.Contract,
      Site: res[0].data,
      Meters: res[1].data,
      MeterPoint: res[2].data,
      Registration: res[3].data,
      Readings: readings,
      HhMeterPoint: null,
      Tenancies: serviceTenancies,
    });
  },

  // eslint-disable-next-line
  getSite({ }, payload) {
    return Vue.prototype.$http.get(
      'https://' +
      Vue.config.honeycomb.contractbot.domain +
      '/api/v1/sites/' +
      payload.Service.SiteId,
      {
        withCredentials: true,
      }
    );
  },
  // eslint-disable-next-line
  getMeters({ }, { flowbotDomain, meterPointSharedId, payload }) {
    let query = '/api/v1/meters/?meterPointId=';
    if (payload.Service.ServiceType === 'Electricity')
      query = '/api/v1/meters/?meterPointSharedId=';

    return Vue.prototype.$http.get(
      'https://' + flowbotDomain + query + meterPointSharedId,
      {
        withCredentials: true,
      }
    );
  },
  // eslint-disable-next-line
  getMeterPoint({ }, { flowbotDomain, meterPointSharedId }) {
    return Vue.prototype.$http.get(
      'https://' + flowbotDomain + '/api/v1/MeterPoints/' + meterPointSharedId,
      {
        withCredentials: true,
      }
    );
  },
  // eslint-disable-next-line
  getRegistration({ }, { flowbotDomain, registrationSharedId }) {
    return Vue.prototype.$http.get(
      'https://' +
      flowbotDomain +
      '/api/v1/registrations/' +
      registrationSharedId,
      {
        withCredentials: true,
      }
    );
  },
  // eslint-disable-next-line
  getAdvances({ commit }, { serviceIdentifier, fromDate, toDate }) {
    Vue.prototype.$http
      .get(
        'https://' +
        Vue.config.honeycomb.advancesbot.electricity.domain +
        '/api/v1/BestAdvances?Mpan=' +
        serviceIdentifier +
        '&fromDate=' +
        fromDate +
        '&ToDate=' +
        toDate,
        {
          withCredentials: true,
        }
      )
      .then((response) => {
        commit('updateAdvances', response.data);
      });
  },
  // eslint-disable-next-line
  getAdvancesForDate({ commit }, { serviceIdentifier, date }) {
    Vue.prototype.$http
      .get(
        'https://' +
        Vue.config.honeycomb.advancesbot.electricity.domain +
        '/api/v1/advances/' +
        serviceIdentifier +
        '/' +
        date,
        {
          withCredentials: true,
        }
      )
      .then((response) => {
        commit('updateAdvances', response.data);
      });
  },
  // eslint-disable-next-line
  getHhMeterPoint({ }, meterPointSharedId) {
    return Vue.prototype.$http.get(
      'https://' +
      Vue.config.honeycomb.advancesbot.electricity.domain +
      '/api/v1/meterpoints/' +
      meterPointSharedId,
      {
        withCredentials: true,
      }
    );
  },

  async getSscInfo({ commit }, ssc) {
    try {
      const response = await Vue.prototype.$http.get(
        'https://' +
        Vue.config.honeycomb.flowbot.electricity.domain +
        '/api/v1/Ssc/' +
        ssc,
        {
          withCredentials: true,
        }
      );
      commit('updateSscInfo', response.data);
    } catch (error) {
      if (error.response.data.HttpCode == 404) {
        Vue.prototype.$snotify.error(`SSC not found!`);
      }
    }
  },

  async getAdvancesRAG({ commit, rootState }, { serviceIdentifier }) {
    try {
      const customerReference = rootState.customers.customer.CustomerReference;
      const response = await Vue.prototype.$http.get(
        'https://' +
        Vue.config.honeycomb.queenbee.domain +
        '/api/v1/Advances/range?customerReference=' + customerReference +
        '&serviceIdentifier=' + serviceIdentifier,
        {
          withCredentials: true,
        }
      );
      commit('setAdvanceRAGRanges', response.data.AdvancesRanges);
      commit('setAdvanceRangesAvailable', true);
    } catch (error) {
      if (error.response.data.HttpCode == 404) {
        Vue.prototype.$snotify.error(`Advance data not found!`);
        commit('setAdvanceRangesAvailable', false);
      } else {
        commit('setAdvanceRangesAvailable', null); // Reset to unknown on other errors
      }
      commit('setAdvanceRAGRanges', []);
    }
  },
}

export const getters = {
  isCurrentlyHH: (state) => {
    if (state.activeService && state.activeService.MeterPoint && state.activeService.MeterPoint.MeterPointConfigs) {
      const latestConfig = _.sortBy(state.activeService.MeterPoint.MeterPointConfigs, 'EffectiveFrom').reverse()[0];
      return latestConfig && latestConfig.MeasurementMethod === 'HalfHourly';
    }
    return false;
  },
  nhhMeters: (state) => {
    if (!state.activeService) return [];

    if (state.activeService.Service.ServiceType === 'Gas') {
      return state.activeService.Meters;
    }

    if (state.activeService.Service.ServiceType === 'Electricity') {
      return state.activeService.Meters.filter(meter => {
        const meterInstalled = moment(meter.Installed);
        const meterRemoved = meter.Removed ? moment(meter.Removed) : moment().add(100, 'years');

        return state.activeService.MeterPoint.MeterPointConfigs.some(mpConfig => {
          if (mpConfig.MeasurementMethod === 'NonHalfHourly') {
            const mpConfigEffectiveFrom = moment(mpConfig.EffectiveFrom);
            const mpConfigEffectiveTo = mpConfig.EffectiveTo ? moment(mpConfig.EffectiveTo) : moment().add(100, 'years');
            return meterInstalled.isSameOrBefore(mpConfigEffectiveTo) && meterRemoved.isSameOrAfter(mpConfigEffectiveFrom);
          }
          return false;
        });
      });
    }

    return [];
  },
  hasHistoricNHHReadings: (state, getters) => {
    if (!state.activeService || !state.activeService.Readings) {
      return false;
    }

    if (state.activeService.Service.ServiceType === 'Gas') {
      return state.activeService.Readings.length > 0;
    } else if (state.activeService.Service.ServiceType === 'Electricity') {
      const nhhMeterSerials = getters.nhhMeters.map((meter) => meter.Serial);
      return state.activeService.Readings.some((reading) =>
        nhhMeterSerials.includes(reading.Serial)
      );
    }
    return false;
  },
  activeServiceType: (state) => {
    if (!state.activeService) return null;
    return state.activeService.Service.ServiceType;
  },
  measurementMethod: (state) => {
    if (
      !state.activeService ||
      !state.activeService.MeterPoint.MeterPointConfigs
    )
      return null;
    return _.sortBy(
      state.activeService.MeterPoint.MeterPointConfigs,
      'EffectiveFrom'
    ).reverse()[0].MeasurementMethod;
  },
  hasHistoricReadings: (state) => {
    return (
      state.activeService &&
      state.activeService.Readings &&
      state.activeService.Readings.length > 0
    );
  },
  hasEverBeenHH: (state) => {
    if (!state.activeService || !state.activeService.MeterPoint || !state.activeService.MeterPoint.MeterPointConfigs) {
      return false;
    }

    // Check if any meter point config has ever been Half Hourly
    return state.activeService.MeterPoint.MeterPointConfigs.some(
      config => config.MeasurementMethod === 'HalfHourly'
    );
  },
  hasEverBeenNHH: (state) => {
    if (!state.activeService || !state.activeService.MeterPoint || !state.activeService.MeterPoint.MeterPointConfigs) {
      return false;
    }

    // Check measurement method history to identify CoMC customers
    return state.activeService.MeterPoint.MeterPointConfigs.some(
      config => config.MeasurementMethod === 'NonHalfHourly'
    );
  },
  hasHistoricAdvances: (state, getters) => {
    // Only show historic advances if:
    // 1. Service has confirmed advance ranges available AND
    // 2. Service has ever been Half Hourly (not just currently HH)
    return state.activeService &&
           state.advanceRangesAvailable === true &&
           getters.hasEverBeenHH;
  },
  showReadingsTab: (state, getters) => {
    if (!state.activeService) return false;
    return !getters.isCurrentlyHH || getters.hasHistoricReadings;
  },
  showAdvancesTab: (state, getters) => {
    if (!state.activeService) return false;

    // Show advances tab if customer has ever been HH (measurement method history)
    // The QueenBee data availability doesn't affect tab visibility - only content
    return getters.hasEverBeenHH;
  },
  readingsTabLabel: (state, getters) => {
    return getters.isCurrentlyHH && getters.hasHistoricReadings
      ? 'Historic Readings'
      : 'Readings';
  },
  advancesTabLabel: (state, getters) => {
    // Show "Historic Advances" only if currently NHH but has been HH before
    return !getters.isCurrentlyHH && getters.hasHistoricAdvances
      ? 'Historic Advances'
      : 'Advances';
  },
  meterSerialFilter: (state, getters) => {
    let currentFilter = state.meterSerialFilter;

    if (getters.activeServiceType === 'Electricity') {
      // If the current filter is not 'All' and is not in the list of NHH meters, reset it.
      if (
        currentFilter !== 'All' &&
        !getters.nhhMeters.some((meter) => meter.Serial === currentFilter)
      ) {
        currentFilter = 'All';
      }
    }

    // If no filter is selected ('All' or initial empty state) and there are NHH meters,
    // default to the first NHH meter's serial.
    if (
      (currentFilter === 'All' || !currentFilter) &&
      getters.nhhMeters.length > 0
    ) {
      return getters.nhhMeters[0].Serial;
    }

    return currentFilter;
  },
  canAddReadingToSelectedMeter: (state, getters) => {
    if (getters.activeServiceType === 'Gas') {
      return true; // Always allow adding readings for Gas
    } else if (getters.activeServiceType === 'Electricity') {
      // CoMC (Change of Measurement Class): Customers who transitioned NHH→HH
      // can add historical readings for their previous NHH periods
      if (getters.isCurrentlyHH && getters.hasEverBeenNHH) {
        return true; // CoMC customer can add historical readings
      }

      // Original logic for standard cases
      const selectedMeterSerial = getters.meterSerialFilter;
      if (selectedMeterSerial === 'All') {
        // If 'All' is selected, and there are NHH meters, allow adding.
        return getters.nhhMeters.length > 0;
      } else {
        // Check if the selected meter is in the list of NHH meters
        return getters.nhhMeters.some(
          (meter) => meter.Serial === selectedMeterSerial
        );
      }
    }
    return false;
  },
  getNhhMetersForService: (state) => (service) => {
    if (service.Service.ServiceType === 'Gas') {
      return service.Meters; // All gas meters are relevant
    } else if (service.Service.ServiceType === 'Electricity') {
      return service.Meters.filter(meter => {
        const meterInstalled = moment(meter.Installed);
        const meterRemoved = meter.Removed ? moment(meter.Removed) : moment().add(100, 'years');

        const isNhhMeter = service.MeterPoint.MeterPointConfigs.some(mpConfig => {
          if (mpConfig.MeasurementMethod === 'NonHalfHourly') {
            const mpConfigEffectiveFrom = moment(mpConfig.EffectiveFrom);
            const mpConfigEffectiveTo = mpConfig.EffectiveTo ? moment(mpConfig.EffectiveTo) : moment().add(100, 'years');
            return meterInstalled.isSameOrBefore(mpConfigEffectiveTo) && meterRemoved.isSameOrAfter(mpConfigEffectiveFrom);
          }
          return false;
        });
        return isNhhMeter;
      });
    }
    return [];
  },
  readingsPanelMenu: (state, getters) => (services) => {
    let meterList = [];
    services.forEach((service) => {
      var serviceType = service.Service.ServiceType;
      let metersToConsider = getters.getNhhMetersForService(service);

      let meters = _.map(metersToConsider, function (meter) {
        if (serviceType == 'Gas') {
          return {
            menuType: 'Readings',
            title:
              'MPRN: ' + service.MeterPoint.Mprn + ' Serial: ' + meter.Serial,
            pointer: true,
            meter: {
              MeterPointSharedId: meter.MeterPointSharedId,
              ServiceType: serviceType,
              Serial: meter.Serial,
              Dials: meter.Dials,
              Mprn: service.MeterPoint.Mprn,
              Removed: meter.Removed,
              Installed: meter.Installed,
              MeterConfigs: meter.MeterConfigs,
              LatestReading:
                service.Readings.length > 0
                  ? _.sortBy(
                    _.filter(service.Readings, function (reading) {
                      return reading.Serial == meter.Serial;
                    }),
                    'ReadingDate'
                  ).reverse()[0]
                  : null,
            },
          };
        }
        if (serviceType == 'Electricity') {
          return {
            menuType: 'Readings',
            title:
              'MPAN: ' + service.MeterPoint.Mpan + ' Serial: ' + meter.Serial,
            pointer: true,
            meter: {
              MeterPointSharedId: meter.MeterPointSharedId,
              ServiceType: serviceType,
              Serial: meter.Serial,
              Mpan: service.MeterPoint.Mpan,
              Removed: meter.Removed,
              Installed: meter.Installed,
              MeterConfigs: meter.MeterConfigs,
              LatestReading:
                service.Readings.length > 0
                  ? _.sortBy(
                    _.filter(service.Readings, function (reading) {
                      return reading.Serial == meter.Serial;
                    }),
                    'ReadingDate'
                  ).reverse()[0]
                  : null,
              Config: _.sortBy(
                meter.MeterConfigs,
                'EffectiveFrom'
              ).reverse()[0],
            },
          };
        }
      });

      meters.forEach((meter) => {
        meterList.push(meter);
      });
    });
    return _.sortBy(_.uniq(meterList), 'ReadingDate').reverse();
  }
};

export default {
  state,
  mutations,
  actions,
  getters
};